import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../../store';
import { registerUser } from '../../../store/thunks';
import './Register.css';

const Register: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    companyName: '',
    password: '',
    confirmPassword: '',
    agreeToTerms: false,
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isLoading, setIsLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    if (errors.general) setErrors(prev => ({ ...prev, general: '' }));
  };

  const validateForm = () => {
    const e: { [k: string]: string } = {};
    if (!formData.firstName.trim()) e.firstName = 'First name is required';
    if (!formData.lastName.trim()) e.lastName = 'Last name is required';
    if (!formData.email) e.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) e.email = 'Enter a valid email';
    if (!formData.phone.trim()) e.phone = 'Phone number is required';
    else if (!/^[6-9]\d{9}$/.test(formData.phone.replace(/\s/g, ''))) e.phone = 'Enter a valid 10-digit Indian number';
    if (!formData.companyName.trim()) e.companyName = 'Company name is required';
    if (!formData.password) e.password = 'Password is required';
    else if (formData.password.length < 6) e.password = 'Minimum 6 characters';
    if (formData.password !== formData.confirmPassword) e.confirmPassword = 'Passwords do not match';
    if (!formData.agreeToTerms) e.agreeToTerms = 'You must accept the Terms & Conditions';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsLoading(true);
    try {
      const result = await dispatch(registerUser({
        email: formData.email,
        password: formData.password,
        name: `${formData.firstName} ${formData.lastName}`.trim(),
      }));
      if (registerUser.fulfilled.match(result)) {
        setSuccessMsg('Account created! Redirecting to Sign In...');
        setTimeout(() => navigate('/auth/login', { state: { registeredEmail: formData.email } }), 2000);
      } else {
        setErrors({ general: (result.payload as string) || 'Registration failed. Please try again.' });
      }
    } catch {
      setErrors({ general: 'Something went wrong. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  const passwordStrength = (): { level: number; label: string; color: string } => {
    const p = formData.password;
    if (!p) return { level: 0, label: '', color: '' };
    let score = 0;
    if (p.length >= 6) score++;
    if (p.length >= 10) score++;
    if (/[A-Z]/.test(p)) score++;
    if (/[0-9]/.test(p)) score++;
    if (/[^A-Za-z0-9]/.test(p)) score++;
    if (score <= 1) return { level: 1, label: 'Weak', color: '#ef4444' };
    if (score <= 3) return { level: 2, label: 'Fair', color: '#f59e0b' };
    if (score === 4) return { level: 3, label: 'Good', color: '#10b981' };
    return { level: 4, label: 'Strong', color: '#059669' };
  };

  const strength = passwordStrength();

  return (
    <div className="auth-page">
      {/* Left Branding */}
      <div className="auth-left">
        <div className="auth-brand">
          <div className="auth-logo">
            <svg width="36" height="36" viewBox="0 0 40 40" fill="none">
              <rect width="40" height="40" rx="10" fill="#fff" fillOpacity="0.15"/>
              <path d="M8 28L14 12L20 22L26 16L32 28H8Z" fill="white"/>
            </svg>
            <span>IndustryBull</span>
          </div>
          <div className="auth-tagline">
            <h2>Join India's Largest B2B Industrial Platform</h2>
            <p>Get access to exclusive bulk pricing, verified suppliers, and a full product catalog.</p>
          </div>
          <div className="register-benefits">
            {[
              { icon: '🏭', title: 'Verified Suppliers', desc: 'Sourced from trusted manufacturers' },
              { icon: '💰', title: 'Bulk Discounts', desc: 'Special B2B pricing on every order' },
              { icon: '📦', title: 'Fast Delivery', desc: 'Pan-India shipping within 48–72 hrs' },
              { icon: '🔒', title: 'Secure Payments', desc: 'PCI-DSS compliant payment gateway' },
            ].map(b => (
              <div key={b.title} className="benefit-item">
                <span className="benefit-icon">{b.icon}</span>
                <div>
                  <strong>{b.title}</strong>
                  <p>{b.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Form */}
      <div className="auth-right register-right">
        <div className="auth-card">
          <div className="auth-card-header">
            <h1>Create B2B Account</h1>
            <p>Free registration · No credit card required</p>
          </div>

          {successMsg && (
            <div className="auth-success-banner">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
              {successMsg}
            </div>
          )}

          {errors.general && (
            <div className="auth-error-banner">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              {errors.general}
            </div>
          )}

          <form onSubmit={handleSubmit} className="auth-form" noValidate>
            {/* Name Row */}
            <div className="form-row-two">
              <div className={`auth-field ${errors.firstName ? 'has-error' : ''}`}>
                <label htmlFor="firstName">First Name *</label>
                <div className="auth-input-wrap">
                  <input id="firstName" name="firstName" type="text" value={formData.firstName}
                    onChange={handleChange} placeholder="Rahul" disabled={isLoading} />
                </div>
                {errors.firstName && <span className="field-error">{errors.firstName}</span>}
              </div>
              <div className={`auth-field ${errors.lastName ? 'has-error' : ''}`}>
                <label htmlFor="lastName">Last Name *</label>
                <div className="auth-input-wrap">
                  <input id="lastName" name="lastName" type="text" value={formData.lastName}
                    onChange={handleChange} placeholder="Sharma" disabled={isLoading} />
                </div>
                {errors.lastName && <span className="field-error">{errors.lastName}</span>}
              </div>
            </div>

            {/* Email */}
            <div className={`auth-field ${errors.email ? 'has-error' : ''}`}>
              <label htmlFor="reg-email">Business Email *</label>
              <div className="auth-input-wrap">
                <svg className="input-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                  <polyline points="22,6 12,13 2,6"/>
                </svg>
                <input id="reg-email" name="email" type="email" value={formData.email}
                  onChange={handleChange} placeholder="buyer@company.com" disabled={isLoading} autoComplete="email" />
              </div>
              {errors.email && <span className="field-error">{errors.email}</span>}
            </div>

            {/* Phone */}
            <div className={`auth-field ${errors.phone ? 'has-error' : ''}`}>
              <label htmlFor="reg-phone">Mobile Number *</label>
              <div className="auth-input-wrap phone-input-wrap">
                <span className="phone-prefix">🇮🇳 +91</span>
                <input id="reg-phone" name="phone" type="tel" value={formData.phone}
                  onChange={handleChange} placeholder="98765 43210" maxLength={10} disabled={isLoading} />
              </div>
              {errors.phone && <span className="field-error">{errors.phone}</span>}
            </div>

            {/* Company */}
            <div className={`auth-field ${errors.companyName ? 'has-error' : ''}`}>
              <label htmlFor="companyName">Company / Business Name *</label>
              <div className="auth-input-wrap">
                <svg className="input-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                  <polyline points="9 22 9 12 15 12 15 22"/>
                </svg>
                <input id="companyName" name="companyName" type="text" value={formData.companyName}
                  onChange={handleChange} placeholder="Apex Industrial Solutions Pvt. Ltd." disabled={isLoading} />
              </div>
              {errors.companyName && <span className="field-error">{errors.companyName}</span>}
            </div>

            {/* Password Row */}
            <div className="form-row-two">
              <div className={`auth-field ${errors.password ? 'has-error' : ''}`}>
                <label htmlFor="reg-password">Password *</label>
                <div className="auth-input-wrap">
                  <input id="reg-password" name="password" type={showPassword ? 'text' : 'password'}
                    value={formData.password} onChange={handleChange} placeholder="Min 6 chars" disabled={isLoading} />
                  <button type="button" className="toggle-password" onClick={() => setShowPassword(p => !p)} tabIndex={-1}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      {showPassword
                        ? <><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></>
                        : <><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></>}
                    </svg>
                  </button>
                </div>
                {formData.password && (
                  <div className="password-strength">
                    <div className="strength-bars">
                      {[1,2,3,4].map(i => (
                        <div key={i} className="strength-bar" style={{ background: i <= strength.level ? strength.color : '#e2e8f0' }} />
                      ))}
                    </div>
                    <span style={{ color: strength.color, fontSize: '12px', fontWeight: 600 }}>{strength.label}</span>
                  </div>
                )}
                {errors.password && <span className="field-error">{errors.password}</span>}
              </div>

              <div className={`auth-field ${errors.confirmPassword ? 'has-error' : ''}`}>
                <label htmlFor="confirmPassword">Confirm Password *</label>
                <div className="auth-input-wrap">
                  <input id="confirmPassword" name="confirmPassword" type={showConfirm ? 'text' : 'password'}
                    value={formData.confirmPassword} onChange={handleChange} placeholder="Repeat password" disabled={isLoading} />
                  <button type="button" className="toggle-password" onClick={() => setShowConfirm(p => !p)} tabIndex={-1}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      {showConfirm
                        ? <><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></>
                        : <><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></>}
                    </svg>
                  </button>
                </div>
                {errors.confirmPassword && <span className="field-error">{errors.confirmPassword}</span>}
              </div>
            </div>

            {/* Terms */}
            <div className={`terms-group ${errors.agreeToTerms ? 'has-error' : ''}`}>
              <label className="checkbox-row">
                <input type="checkbox" name="agreeToTerms" checked={formData.agreeToTerms}
                  onChange={handleChange} disabled={isLoading} />
                <span>
                  I agree to IndustryBull's{' '}
                  <a href="/terms" target="_blank" rel="noreferrer" className="auth-link">Terms of Service</a>
                  {' '}&amp;{' '}
                  <a href="/privacy" target="_blank" rel="noreferrer" className="auth-link">Privacy Policy</a>
                </span>
              </label>
              {errors.agreeToTerms && <span className="field-error">{errors.agreeToTerms}</span>}
            </div>

            <button type="submit" className="auth-submit-btn" disabled={isLoading}>
              {isLoading ? (
                <><span className="btn-spinner" />&nbsp;Creating Account...</>
              ) : 'Create Free B2B Account'}
            </button>
          </form>

          <p className="auth-footer-text" style={{ marginTop: 20 }}>
            Already have an account?{' '}
            <Link to="/auth/login" className="auth-link">Sign In →</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;
